/**
 * Transformaciones del cuerpo del artículo: sacar el ad legacy y marcar la
 * posición del slot de CTA in-body (R10).
 *
 * Están acá y no en el consumidor para que la regla de clasificación sea una
 * función pura y enumerable: son 24 URLs contra 2.076 apariciones de la forma
 * `<a><img></a>` en el catálogo, y dos de esas clases se le escapan a una
 * implementación ingenua (el href con espacios adentro del atributo y los
 * subdominios propios).
 */

/**
 * Hosts propios: `olagg.io` y cualquier subdominio, el acortador `olagg.link` y
 * el deep link `olagaming.app.link`, que también es nuestro y llevaba a La Garra.
 */
const OWN_HOST = /(^|\.)olagg\.io$|(^|\.)olagg\.link$|(^|\.)olagaming\.app\.link$/i

/**
 * Una ruta de contenido se preserva, con o sin prefijo de idioma: no es un ad, es
 * un link a otra cosa que escribimos nosotros.
 */
const ARTICLE_PATH = /(^|\/)(novedades|guias|posts)\//i

/** Marcador que el renderer intercambia por el componente de CTA. */
export const CTA_SLOT_ATTRIBUTE = 'data-olagg-cta'

/**
 * Marca la raiz del CTA ya renderizado. El CTA del medio del cuerpo vive DENTRO
 * del markdown, asi que sin esto hereda la tipografia del articulo: el margen de
 * las imagenes deja el arte flotando con una banda de color alrededor, y el
 * margen de los parrafos abre el titulo, el subtitulo y el boton.
 */
export const CTA_ROOT_ATTRIBUTE = 'data-olagg-cta-root'

/**
 * El primer límite de bloque que pasa este porcentaje del texto acumulado es
 * donde se monta `in_body_mid`. El 30% no es arbitrario: es donde está hoy la
 * mediana del primer ad legacy en los artículos que tienen uno o dos. Y es una
 * constante, no la posición del ad de cada artículo: si el slot heredara esas
 * coordenadas su posición variaría artículo por artículo y pasaría a ser una
 * variable confundida dentro del experimento.
 */
export const IN_BODY_SLOT_OFFSET = 0.3

type HastNode = {
  type: string
  tagName?: string
  value?: string
  properties?: Record<string, any>
  children?: HastNode[]
}

const isElement = (node: HastNode, tagName: string) =>
  node.type === 'element' && node.tagName === tagName

/** Los nodos de texto en blanco entre etiquetas no cuentan como contenido. */
const meaningfulChildren = (node: HastNode) =>
  (node.children || []).filter(child =>
    !(child.type === 'text' && !(child.value || '').trim())
  )

/**
 * Un ad propio del cuerpo: un `<a>` cuyo único hijo es un `<img>` y que apunta a
 * un host propio que no es una ruta de artículo.
 *
 * El `trim()` del href no es cosmético. El ad más frecuente del catálogo es
 * `https://olagg.io/es/mini-juegos/claw-ola` con 800 apariciones, y 572 traen el
 * href con espacios adentro del atributo: sin `trim` se escapa el 30% de todos
 * los ads.
 */
export const isLegacyOwnAd = (node: HastNode): boolean => {
  if (!isElement(node, 'a')) return false

  const children = meaningfulChildren(node)

  if (children.length !== 1 || !isElement(children[0], 'img')) return false

  const href = String(node.properties?.href ?? '').trim()

  if (!href) return false

  try {
    const url = new URL(href, 'https://olagg.io')

    if (!OWN_HOST.test(url.hostname)) return false

    return !ARTICLE_PATH.test(url.pathname)
  } catch {
    return false
  }
}

const walk = (node: HastNode, visit: (parent: HastNode) => void) => {
  if (!node.children?.length) return

  visit(node)
  node.children.forEach(child => walk(child, visit))
}

/**
 * Descarta los ads propios del cuerpo. Detrás de un interruptor: se pasa
 * `enabled: false` y el cuerpo vuelve a renderizarse como hoy.
 */
export const rehypeDropLegacyAds = (options?: { enabled?: boolean }) => (tree: HastNode) => {
  if (options?.enabled === false) return tree

  walk(tree, parent => {
    parent.children = (parent.children || []).filter(child => !isLegacyOwnAd(child))
  })

  return tree
}

const textLength = (node: HastNode): number => {
  if (node.type === 'text') return (node.value || '').length

  return (node.children || []).reduce((total, child) => total + textLength(child), 0)
}

/**
 * Inserta el marcador del slot in-body en el primer límite de bloque de nivel
 * superior que pase `IN_BODY_SLOT_OFFSET` de la longitud acumulada del texto.
 * Igual para todos los artículos, sin importar cuántos ads legacy tuvieran.
 */
export const rehypeInsertCtaSlot = (options?: { slot?: string | null }) => (tree: HastNode) => {
  const slot = options?.slot

  if (!slot) return tree

  const blocks = tree.children || []
  const total = blocks.reduce((sum, block) => sum + textLength(block), 0)

  if (!total) return tree

  const threshold = total * IN_BODY_SLOT_OFFSET
  let accumulated = 0
  let insertAt = blocks.length

  for (let index = 0; index < blocks.length; index += 1) {
    accumulated += textLength(blocks[index])

    if (accumulated >= threshold) {
      insertAt = index + 1
      break
    }
  }

  const marker: HastNode = {
    type: 'element',
    tagName: 'div',
    properties: {[CTA_SLOT_ATTRIBUTE]: slot},
    children: []
  }

  tree.children = [...blocks.slice(0, insertAt), marker, ...blocks.slice(insertAt)]

  return tree
}
